import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  ThemeProvider,
  createTheme,
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Grid,
  CardContent,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import theme from "../../../styles/theme/theme";
import { SendRounded } from "@mui/icons-material";
import { SellerToShipForm } from "./shippingForms/SellerToShipForm";
import { SelfPickupForm } from "./shippingForms/SelfPickupForm.jsx";
import { ThirdPartyShippingForm } from "./shippingForms/ThirdPartyShippingForm.jsx";
import { styled } from "@mui/material/styles";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TruckPickupIcon from "@mui/icons-material/LocalShippingOutlined";
import AuthButton from "../../../components/auth/AuthButton.jsx";
import ImportantNotes from "./shippingForms/ImportantNotes.jsx";
import { selfPickupSchema } from "./validations/selfPickupschema.js";
import { sellerToShipSchema } from "./validations/sellerToShipschema.js";
import { thirdPartyShippingSchema } from "./validations/thirdPartyschema.js";
import { useDispatch, useSelector } from "react-redux";
import {
  adminAssistance,
  fetchOrderDetails,
  orderShippingDetails,
  resetAdminAssistance,
} from "../../../features/buyer/buyerSlice.js";
import Loader from "../../../components/common/Loader.jsx";
import { showAppMessageToast } from "../../../utils/toastUtils.js";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { placeOrder } from "../../../features/buyer/buyerSlice";
import OrderConfirmModal from "./components/OrderConfirmModal";

// Define the shipping modes with similar structure
const MODES = [
  {
    value: "self_pickup",
    label: "Self Pickup",
    caption: "Buyer collects from seller location",
    Icon: StorefrontIcon,
  },
  {
    value: "third_party",
    label: "Third-Party",
    caption: "Partner picks up & delivers",
    Icon: TruckPickupIcon,
  },
  {
    value: "seller_ship",
    label: "Seller to Ship",
    caption: "Seller arranges shipping",
    Icon: LocalShippingIcon,
  },
];

// Styled components (same as your ShippingModeSelector)
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: "24px",
  margin: "0 auto",
  maxWidth: "1200px",
  borderRadius: "14px",
  boxShadow: "0px 2px 10px rgba(0,0,0,0.06)",
  marginBottom: "28px",
}));

const StyledModeCard = styled(Paper)(({ theme, active }) => ({
  position: "relative",
  cursor: "pointer",
  padding: "24px",
  flex: 1,
  display: "flex",
  alignItems: "center",
  gap: "20px",
  borderRadius: "16px",
  overflow: "hidden",
  border: active ? "2px solid #1976d2" : "2px solid rgba(0, 0, 0, 0.08)",
  backgroundColor: active ? "rgba(25, 118, 210, 0.05)" : "#ffffff",
  backgroundImage: active
    ? "linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.03) 100%)"
    : "none",
  boxShadow: active
    ? "0 8px 24px rgba(25, 118, 210, 0.15)"
    : "0 2px 8px rgba(0, 0, 0, 0.06)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  transform: active ? "translateY(-2px)" : "translateY(0)",
  "&:hover": {
    borderColor: active ? "#1976d2" : "rgba(25, 118, 210, 0.4)",
    boxShadow: active
      ? "0 12px 32px rgba(25, 118, 210, 0.2)"
      : "0 4px 16px rgba(0, 0, 0, 0.1)",
    transform: "translateY(-4px)",
    backgroundColor: active
      ? "rgba(25, 118, 210, 0.08)"
      : "rgba(25, 118, 210, 0.02)",
  },
}));

const IconBox = styled(Box)(({ active }) => ({
  width: "56px",
  height: "56px",
  minWidth: "56px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "12px",
  backgroundColor: active ? "rgba(25, 118, 210, 0.15)" : "rgba(0, 0, 0, 0.04)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: active
      ? "rgba(25, 118, 210, 0.25)"
      : "rgba(0, 0, 0, 0.08)",
    transform: "scale(1.05)",
  },
  "& svg": {
    width: "28px",
    height: "28px",
    color: active ? "#1976d2" : "#718096",
    transition: "color 0.3s ease",
  },
}));

const ModeContent = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const ModeLabel = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: "700",
  fontSize: "15px",
  color: "#1a1a1a",
  letterSpacing: "-0.3px",
});

const ModeCaption = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: "500",
  fontSize: "13px",
  color: "#718096",
  lineHeight: "1.4",
});

const ActiveBadge = styled(Box)({
  padding: "4px 10px",
  borderRadius: "6px",
  backgroundColor: "rgba(25,118,210,0.1)",
  color: "#1976d2",
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
});

const muiTheme = createTheme(theme);

export default function ShippingModule() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromBuyNow = location.state?.fromBuyNow;
  const productParam = location.state?.productParam;

  const [shippingMode, setShippingMode] = useState("self_pickup");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const dispatch = useDispatch();
  const {
    orderShippingLoading,
    orderShippingError,
    orderShippingSuccess,
    orderDetails,
  } = useSelector((state) => state.buyer);
  const order = useSelector((state) => state.buyer.orderDetails);
  const incoterm = order?.product?.incoterms;

  useEffect(() => {
    if (id) {
      dispatch(fetchOrderDetails(id));
    }
  }, [id, dispatch]);

  const SHIPPING_BY_INCOTERM = {
    EXW: ["self_pickup", "third_party"],
    FOB: ["self_pickup", "third_party"],
    DDP: ["seller_ship"],
  };

  const allowedModes = useMemo(() => {
    return SHIPPING_BY_INCOTERM[incoterm] || [];
  }, [incoterm]);

  useEffect(() => {
    if (allowedModes.length > 0 && !allowedModes.includes(shippingMode)) {
      setShippingMode(allowedModes[0]);
    }
  }, [allowedModes, shippingMode]);

  console.log("Order detail:", order);
  console.log("Product incoterms:", incoterm);
  const {
    adminAssistance: {
      loading: adminAssistanceLoading,
      success: adminAssistanceSuccess,
      error: adminAssistanceError,
    },
  } = useSelector((state) => state.buyer);

  const [outOfRangeData, setOutOfRangeData] = useState(null);
  const [showOutOfRangeAlert, setShowOutOfRangeAlert] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger, // To trigger validation
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      shippingMode: "self_pickup",
      pickupType: "own",
      vehicleInfo: {
        vehicleType: "",
        registrationNumber: "",
        driverName: "",
        driverPhone: "",
        carrier: "",
        confirmationNumber: "",
        consignmentNote: "",
      },
      pickupAddress: {
        firstName: "", // Same for deliveryAddress fields
        lastName: "",
        phone: "",
        companyName: "",
        taxId: "",
        country: "",
        state: "",
        city: "",
        postcode: "",
        addressLine1: "",
        addressLine2: "",
        locationType: "",
        forkliftAvailability: "",
        noAssistanceAtDelivery: "",
        startDate: "",
        endDate: "",
        pickupTimeSlot: "",
        pickupNotes: "",
      },
      transportDocuments: {
        deliveryNote: "",
        packingList: "",
        weightSlip: "",
        invoice: "",
        consignmentNote: "",
        manifest: "",
        trackingNotes: "",
      },
      billingAddress: {
        firstName: "", // Ensure these fields are initialized properly
        lastName: "",
        phone: "",
        email: "",
        companyName: "",
        taxId: "",
        country: "",
        state: "",
        city: "",
        postcode: "",
        addressLine1: "",
        addressLine2: "",
        landmark: "",
      },
      deliveryAddress: {
        firstName: "", // Same for deliveryAddress fields
        lastName: "",
        phone: "",
        companyName: "",
        taxId: "",
        country: "",
        state: "",
        city: "",
        postcode: "",
        addressLine1: "",
        addressLine2: "",
        locationType: "",
        forkliftAvailability: "",
        noAssistanceAtDelivery: "",
        startDate: "",
        endDate: "",
        deliveryTimeSlot: "",
        deliveryNotes: "",
      },
    },
    // resolver: yupResolver(schemaByMode[shippingMode]),
    resolver: undefined,
    mode: "onSubmit",
  });

  useEffect(() => {
    reset({
      shippingMode,
      pickupType: "own", // Default to 'own' on page load
      vehicleInfo: {
        vehicleType: "",
        registrationNumber: "",
        driverName: "",
        driverPhone: "",
        carrier: "",
        confirmationNumber: "",
        consignmentNote: "",
      },
      billingAddress: {
        firstName: "", // Ensure these fields are initialized properly
        lastName: "",
        phone: "",
        email: "",
        companyName: "",
        taxId: "",
        country: "",
        state: "",
        city: "",
        postcode: "",
        addressLine1: "",
        addressLine2: "",
        landmark: "",
      },
      deliveryAddress: {
        firstName: "", // Same for deliveryAddress fields
        lastName: "",
        phone: "",
        companyName: "",
        taxId: "",
        country: "",
        state: "",
        city: "",
        postcode: "",
        addressLine1: "",
        addressLine2: "",
        locationType: "",
        forkliftAvailability: "",
        noAssistanceAtDelivery: "",
        specialAssistanceNotes: "",
        startDate: "",
        endDate: "",
        deliveryTimeSlot: "",
        deliveryNotes: "",
      },
      pickupAddress: {
        firstName: "", // Same for deliveryAddress fields
        lastName: "",
        phone: "",
        companyName: "",
        taxId: "",
        country: "",
        state: "",
        city: "",
        postcode: "",
        addressLine1: "",
        addressLine2: "",
        locationType: "",
        forkliftAvailability: "",
        noAssistanceAtPickup: "",
        specialAssistanceNotes: "",
        startDate: "",
        endDate: "",
        pickupTimeSlot: "",
        pickupNotes: "",
      },
      transportDocuments: {
        deliveryNote: "",
        packingList: "",
        weightSlip: "",
        invoice: "",
        consignmentNote: "",
        manifest: "",
        trackingNotes: "",
      },
    });
    // setValue("pickupType", pickupType);
  }, [shippingMode, reset]);

  useEffect(() => {
    if (orderShippingSuccess) {
      showAppMessageToast({
        type: "success",
        title: "Shipment Saved",
        message: "Shipment details submitted successfully.",
      });
    }
  }, [orderShippingSuccess]);

  useEffect(() => {
    if (orderShippingError) {
      let message = "Failed to submit shipment details.";

      if (typeof orderShippingError === "string") {
        message = orderShippingError;
      } else if (orderShippingError?.errors) {
        message = Object.values(orderShippingError.errors).flat().join(", ");
      }

      showAppMessageToast({
        type: "error",
        title: "Submission Failed",
        message,
      });
    }
  }, [orderShippingError]);

  // useEffect(() => {
  //   // Trigger validation whenever pickupType changes
  //   trigger();
  // }, [pickupType, trigger]);

  const appendBillingAddress = (formData, billing) => {
    formData.append("billing_address[first_name]", billing.firstName);
    formData.append("billing_address[last_name]", billing.lastName);
    formData.append("billing_address[phone]", billing.phone);
    formData.append("billing_address[email]", billing.email);
    formData.append("billing_address[company_name]", billing.companyName);
    formData.append("billing_address[tax_id]", billing.taxId);
    formData.append("billing_address[country]", billing.country);
    formData.append("billing_address[state]", billing.state);
    formData.append("billing_address[city]", billing.city);
    formData.append("billing_address[postcode]", billing.postcode);
    formData.append("billing_address[address_line_1]", billing.addressLine1);
    formData.append("billing_address[address_line_2]", billing.addressLine2);
    formData.append("billing_address[landmark]", billing.landmark);
  };

  const appendPickupAddress = (formData, pickup) => {
    formData.append("pickup_address[first_name]", pickup.firstName);
    formData.append("pickup_address[last_name]", pickup.lastName);
    formData.append("pickup_address[phone]", pickup.phone);
    formData.append("pickup_address[company_name]", pickup.companyName);
    formData.append("pickup_address[country]", pickup.country);
    formData.append("pickup_address[state]", pickup.state);
    formData.append("pickup_address[city]", pickup.city);
    formData.append("pickup_address[postcode]", pickup.postcode);
    formData.append("pickup_address[address_line_1]", pickup.addressLine1);
    formData.append("pickup_address[address_line_2]", pickup.addressLine2);
    formData.append("pickup_address[location_type]", pickup.locationType);
    formData.append(
      "pickup_address[forklift_availability]",
      pickup.forkliftAvailability
    );
    formData.append(
      "pickup_address[no_assistance_at_pickup]",
      pickup.noAssistanceAtPickup
    );
    formData.append(
      "pickup_address[special_assistance_notes]",
      pickup.specialAssistanceNotes
    );
    formData.append("pickup_address[start_date]", pickup.startDate);
    formData.append("pickup_address[end_date]", pickup.endDate);
    formData.append("pickup_address[pickup_time_slot]", pickup.pickupTimeSlot);
    formData.append("pickup_address[pickup_notes]", pickup.pickupNotes);
  };

  const appendDeliveryAddress = (formData, delivery) => {
    formData.append("delivery_address[first_name]", delivery.firstName);
    formData.append("delivery_address[last_name]", delivery.lastName);
    formData.append("delivery_address[phone]", delivery.phone);
    formData.append("delivery_address[company_name]", delivery.companyName);
    formData.append("delivery_address[tax_id]", delivery.taxId);
    formData.append("delivery_address[country]", delivery.country);
    formData.append("delivery_address[state]", delivery.state);
    formData.append("delivery_address[city]", delivery.city);
    formData.append("delivery_address[postcode]", delivery.postcode);
    formData.append("delivery_address[address_line_1]", delivery.addressLine1);
    formData.append("delivery_address[address_line_2]", delivery.addressLine2);
    formData.append("delivery_address[location_type]", delivery.locationType);
    formData.append(
      "delivery_address[forklift_availability]",
      delivery.forkliftAvailability
    );
    formData.append(
      "delivery_address[no_assistance_at_delivery]",
      delivery.noAssistanceAtDelivery
    );
    formData.append(
      "delivery_address[special_assistance_notes]",
      delivery.specialAssistanceNotes
    );
    formData.append("delivery_address[start_date]", delivery.startDate);
    formData.append("delivery_address[end_date]", delivery.endDate);
    formData.append(
      "delivery_address[delivery_time_slot]",
      delivery.deliveryTimeSlot
    );
    formData.append("delivery_address[delivery_notes]", delivery.deliveryNotes);
  };

  const appendVehicleInfo = (formData, vehicle) => {
    formData.append("vehicle_info[vehicle_type]", vehicle.vehicleType);
    formData.append(
      "vehicle_info[registration_no]",
      vehicle.registrationNumber
    );

    formData.append("driver_info[name]", vehicle.driverName);
    formData.append("driver_info[phone]", vehicle.driverPhone);
    formData.append("driver_info[license]", vehicle.license || "");
    formData.append("confirmation_number", vehicle.confirmationNumber);
    formData.append("carrier", vehicle.carrier);
    formData.append("con_note", vehicle.consignmentNote);
  };

  const appendTransportDocuments = (formData, docs) => {
    formData.append("transport_documents[delivery_note]", docs.deliveryNote);
    formData.append("transport_documents[packing_list]", docs.packingList);
    formData.append("transport_documents[weight_slip]", docs.weightSlip);
    formData.append("transport_documents[invoice]", docs.invoice);
    formData.append(
      "transport_documents[consignment_note]",
      docs.consignmentNote
    );
    formData.append("transport_documents[manifest]", docs.manifest);
  };

  const onSubmit = async (data) => {
    // Trigger validation for the entire form
    const isValid = await trigger();
    if (!isValid) return;

    const formData = new FormData();

    // 1️⃣ Shipping mode (exact backend values)
    if (shippingMode === "seller_ship") {
      formData.append("shipping_mode", "seller_ship");
    } else if (shippingMode === "third_party") {
      formData.append("shipping_mode", "third_party");
    } else {
      formData.append("shipping_mode", "self_pickup");
    }

    // 2️⃣ Billing address (ALL MODES)
    appendBillingAddress(formData, data.billingAddress);

    // 3️⃣ Mode-specific payload
    if (shippingMode === "self_pickup") {
      appendPickupAddress(formData, data.pickupAddress);
      appendVehicleInfo(formData, data.vehicleInfo);
    }

    if (shippingMode === "third_party") {
      appendPickupAddress(formData, data.pickupAddress);
      appendDeliveryAddress(formData, data.deliveryAddress);
      appendTransportDocuments(formData, data.transportDocuments);
    }

    if (shippingMode === "seller_ship") {
      appendDeliveryAddress(formData, data.deliveryAddress);
    }

    if (
      shippingMode === "third_party" &&
      data.transportDocuments.trackingNotes
    ) {
      formData.append("tracking_notes", data.transportDocuments.trackingNotes);
    }

    // Store data and Open Modal if Buy Now flow
    if (fromBuyNow) {
      setPendingFormData(formData);
      setShowConfirmModal(true);
      return;
    }

    // Normal flow
    submitShippingDetails(id, formData);
  };

  const handleModalConfirm = async () => {
    if (!pendingFormData || !productParam) return;

    setIsProcessingOrder(true);
    try {
      // 1. Place Order
      const orderData = { product_id: productParam.id };
      const orderRes = await dispatch(placeOrder(orderData)).unwrap();
      const newOrderId = orderRes.order_id;

      // 2. Submit Shipping using new Order ID
      await submitShippingDetails(newOrderId, pendingFormData);

      setShowConfirmModal(false);
    } catch (error) {
      console.error("Buy Now Flow Failed", error);
      showAppMessageToast({
        type: "error",
        title: "Order Failed",
        message: error.message || "Could not place order.",
      });
    } finally {
      setIsProcessingOrder(false);
    }
  };

  const submitShippingDetails = async (orderId, formData) => {
    // 4️⃣ Debug (remove in production)
    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }
    console.log("id:", orderId);

    // Dispatch the API call here for the POST request
    try {
      const res = await dispatch(
        orderShippingDetails({ id: orderId, data: formData })
      ).unwrap();

      // 🚨 OUT OF RANGE HANDLING
      if (res?.out_of_range === true) {
        setOutOfRangeData(res);
        setShowOutOfRangeAlert(true);
        setIsProcessingOrder(false);
        setShowConfirmModal(false);
        return; // ⛔ STOP navigation
      }

      const otpDataForTesting = {
        otp: res?.otp_for_testing,
        otpVerificationUrl: res?.otp_verification_url,
      };

      navigate(`/order-summary/${orderId}`, {
        state: {
          otpTestData: otpDataForTesting,
        },
      });
    } catch (err) {
      console.error("Shipping API Error:", err);
      // Let existing toast logic handle errors via Redux state if needed,
      // or duplicate toast here. Current logic uses useEffect for success/error.
      // But for the modal flow (which wraps this), errors might need explicit handling if not in Redux.
      // The original code printed alerts or comments.
      if (err.response) {
        // alert("Validation error: " + JSON.stringify(err.response.data));
      }
    }
  };

  useEffect(() => {
    if (adminAssistanceSuccess && showOutOfRangeAlert) {
      setShowOutOfRangeAlert(false);
      dispatch(resetAdminAssistance());
    }
  }, [adminAssistanceSuccess, showOutOfRangeAlert, dispatch]);

  const handleAdminAssistance = async () => {
    try {
      const payload = {
        id,
        data: {
          distance_km: outOfRangeData?.your_distance_km,
        },
      };
      await dispatch(adminAssistance(payload)).unwrap();

      showAppMessageToast({
        type: "success",
        title: "Request Sent",
        message: "Admin assistance request has been sent successfully.",
      });

      setShowOutOfRangeAlert(false);
      dispatch(resetAdminAssistance());

      navigate("/dashboard");
    } catch (err) {
      showAppMessageToast({
        type: "error",
        title: "Request Failed",
        message:
          typeof err === "string"
            ? err
            : "Unable to request admin assistance. Please try again.",
      });
    }
  };

  {
    orderShippingLoading && <Loader />;
  }
  return (
    <>
      <OrderConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleModalConfirm}
        product={productParam}
        loading={isProcessingOrder}
        shippingMode={shippingMode}
      />
      <ThemeProvider theme={muiTheme}>
        <Box sx={{ backgroundColor: "#f9fafb", minHeight: "100vh", py: 4 }}>
          <Container maxWidth="lg">
            {/* HEADER */}
            <Paper elevation={0} sx={{ backgroundColor: "transparent", mb: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Shipping & Delivery Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select a shipping mode and fill in the required details below
              </Typography>
            </Paper>

            {/* SHIPPING MODE SELECTION */}

            <StyledPaper elevation={0}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                {MODES.filter((m) => allowedModes.includes(m.value)) // ← THIS LINE IS POINT 5
                  .map((m) => {
                    const active = m.value === shippingMode;

                    return (
                      <StyledModeCard
                        key={m.value}
                        onClick={() => setShippingMode(m.value)}
                        elevation={0}
                        active={active}
                      >
                        <IconBox active={active}>
                          <m.Icon />
                        </IconBox>

                        <ModeContent>
                          <ModeLabel>{m.label}</ModeLabel>
                          <ModeCaption>{m.caption}</ModeCaption>
                        </ModeContent>

                        {active && <ActiveBadge>Active</ActiveBadge>}
                      </StyledModeCard>
                    );
                  })}
              </Stack>
            </StyledPaper>
            {/* </Paper> */}

            {/* ALERT BOX */}
            <Paper elevation={0} sx={{ mb: 4 }}>
              <Alert severity="info">
                You have selected{" "}
                <strong>
                  {MODES.find((m) => m.value === shippingMode)?.label}
                </strong>
                . Please fill in all the required details below and click
                Submit.
              </Alert>
            </Paper>

            <ImportantNotes />

            <form onSubmit={handleSubmit(onSubmit)}>
              {/* FORM CONTENT */}
              {shippingMode === "seller_ship" && (
                <SellerToShipForm
                  control={control}
                  errors={errors}
                  setValue={setValue}
                />
              )}

              {shippingMode === "self_pickup" && (
                <SelfPickupForm
                  control={control}
                  errors={errors}
                  setValue={setValue}
                />
              )}
              {shippingMode === "third_party" && (
                <ThirdPartyShippingForm
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  trigger={trigger}
                />
              )}

              {/* SUBMIT BUTTON */}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 4,
                  marginRight: "50px",
                }}
              >
                {/* {outOfRangeData?.out_of_range && (
                  <AuthButton
                    variant="outlined"
                    fullWidth={false}
                    style={{ minWidth: 150 }}
                    onClick={() => setShowOutOfRangeAlert(true)}
                  >
                    Admin Assistance
                  </AuthButton>
                )} */}
                <AuthButton
                  variant="contained"
                  fullWidth={false}
                  style={{ minWidth: 150 }}
                  type="submit"
                >
                  Save
                </AuthButton>
              </Box>
            </form>
          </Container>
        </Box>
      </ThemeProvider>
      <Dialog
        open={showOutOfRangeAlert}
        onClose={() => setShowOutOfRangeAlert(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delivery Out of Range</DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {outOfRangeData?.message}
          </Alert>

          <Typography variant="body2">
            Maximum delivery radius: <b>{outOfRangeData?.max_radius_km} km</b>
          </Typography>
          <Typography variant="body2">
            Your delivery distance: <b>{outOfRangeData?.your_distance_km} km</b>
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setShowOutOfRangeAlert(false)}>Close</Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleAdminAssistance}
            disabled={adminAssistanceLoading}
          >
            {adminAssistanceLoading
              ? "Requesting..."
              : "Request Admin Assistance"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
